import { z } from 'zod';
import { wanIpv6Schema } from './wan-ipv6.js';

/**
 * Per-ONU VLAN and port configuration.
 *
 * None of this is in the TR-069 data model on these CPEs — it is delivered over
 * the OLT's CLI in the `pon-onu-mng` context, the same way the platform being
 * replaced does it. SimpleACS stores the intent, renders the commands, and
 * applies them on request.
 */

export const vlanPurposeSchema = z.enum(['internet', 'voip', 'iptv', 'management', 'other']);

/**
 * Whether the ONU hands a VLAN on or terminates it.
 *
 * Not the same question as access/trunk/hybrid, which is about VLAN *tags* on a
 * port. This is about who terminates the connection: a bridged VLAN reaches the
 * subscriber's own router, a routed one stops at the ONU and the ONU serves the
 * LAN behind it. A port can be access-and-bridged or access-and-routed.
 */
export const vlanModeSchema = z.enum(['bridge', 'route']);
export type VlanMode = z.infer<typeof vlanModeSchema>;

/**
 * How the connection on a VLAN gets its address.
 *
 * Picked when the ONU is authorised; the particulars of a static address or a
 * PPPoE account are filled in afterwards, so authorising stays short.
 */
export const vlanAddressingSchema = z.enum(['dhcp', 'static', 'pppoe']);
export type VlanAddressing = z.infer<typeof vlanAddressingSchema>;
export type VlanPurpose = z.infer<typeof vlanPurposeSchema>;

export const VLAN_PURPOSE_LABELS: Record<VlanPurpose, string> = {
  internet: 'Internet',
  voip: 'VoIP',
  iptv: 'IPTV',
  management: 'Management',
  other: 'Other',
};

/**
 * WHO manages a subscriber WAN (operator, 2026-10-07: "set the wan with the
 * option of omci (olt only) or tr069, but the other onu mngt like lan, wifi
 * etc can remain tr069").
 *
 *   omci   the OLT: the chassis line (ZTE `wan-ip`, Huawei `ont ipconfig`) and
 *          nothing over TR-069 for the WAN — the router's own copy is read-only
 *   tr069  the router: its own WAN connection over TR-069, and no chassis
 *          WAN line for it
 *
 * Recorded on the VLAN row the WAN rides. Null — every row before 2026-10-07
 * — means nobody chose, and the platform decides as it always has off the
 * router's own connections (`decideZteSubscriberWriter`). Never both: moving
 * the WAN from one to the other takes it off the first before the second
 * builds it.
 */
export const wanOwnerSchema = z.enum(['omci', 'tr069']);
export type WanOwner = z.infer<typeof wanOwnerSchema>;

export const onuVlanSchema = z.object({
  id: z.string(),
  /** TR-069 owner. Null when the intent belongs to an ONU with no ACS record. */
  deviceId: z.string().nullable(),
  /** OLT-side owner. Exactly one of the two is set. */
  onuId: z.string().nullable().optional(),
  vlanId: z.number().int().min(1).max(4094),
  name: z.string().nullable(),
  purpose: vlanPurposeSchema,
  mode: vlanModeSchema,
  addressing: vlanAddressingSchema,
  staticIp: z.string().nullable().optional(),
  staticMask: z.string().nullable().optional(),
  staticGateway: z.string().nullable().optional(),
  pppoeUsername: z.string().nullable().optional(),
  /** Whether a password is stored — never the password itself. */
  hasPppoePassword: z.boolean().optional(),
  /** Upstream VLAN the OLT translates to, when it differs from the user VLAN. */
  serviceVlanId: z.number().int().min(1).max(4094).nullable(),
  /**
   * The IPv6 half of the subscriber WAN, or null = NOT MANAGED: no IPv6 leaf
   * is written, nothing is compared, no finding is raised. Null is what every
   * row recorded before 2026-09-12 carries, and it has to stay that way —
   * ~130 live subscriber WANs are dual-stack by firmware default with a
   * plain-DHCP record, and a default of "IPv4" would rewrite them. IPv4-only
   * is an explicit choice. See wan-ipv6.ts.
   */
  ipv6: wanIpv6Schema.nullable().optional(),
  /**
   * `primary` | `secondary` on a subscriber (internet) VLAN, null on the rest.
   * The recorded role where there is one, and the platform's rule where there
   * is not — the same answer the line test and a CPE swap act on.
   */
  role: z.enum(['primary', 'secondary']).nullable().optional(),
  /**
   * What the operator calls this VLAN: the label on its OLT's VLANs tab. Null
   * when the OLT has no label for it, and the VLAN is then shown by number.
   */
  label: z.string().nullable().optional(),
  /** Who manages the WAN on this VLAN — see `wanOwnerSchema`. Null = not chosen. */
  wanOwner: wanOwnerSchema.nullable().optional(),
  /**
   * When the WAN on this VLAN was DELETED on purpose (the WAN card's Delete),
   * or null. While set, nothing re-creates it on its own — not a Resync, not
   * a router's first link, not the drift check: the record says there is no
   * WAN here. An Apply of the WAN clears it.
   */
  wanRemovedAt: z.number().int().nullable().optional(),
  createdAt: z.number().int(),
});
export type OnuVlan = z.infer<typeof onuVlanSchema>;

export const createOnuVlanSchema = z.object({
  vlanId: z.coerce.number().int().min(1).max(4094),
  name: z.string().max(60).optional(),
  purpose: vlanPurposeSchema.default('internet'),
  mode: vlanModeSchema.default('bridge'),
  addressing: vlanAddressingSchema.default('dhcp'),
  /**
   * The particulars the addressing implies — a static address, a PPPoE
   * account. The columns waited from the day the schema shipped ("filled in
   * afterwards on the ONU's own page"); the WAN-mode dialog is that page's
   * entry, and it sends them here so the ROW records what the chassis was
   * told. The password is stored encrypted and never returned — the DTO says
   * only `hasPppoePassword`.
   */
  staticIp: z.string().max(64).nullable().optional(),
  staticMask: z.string().max(64).nullable().optional(),
  staticGateway: z.string().max(64).nullable().optional(),
  pppoeUsername: z.string().max(128).nullable().optional(),
  pppoePassword: z.string().max(128).nullable().optional(),
  serviceVlanId: z.coerce.number().int().min(1).max(4094).nullable().optional(),
  /**
   * The IPv6 intent. Absent or null = not managed (the default, and what
   * every existing caller sends). The route runs `ipv6Refusals` against the
   * ONU's target BEFORE storing and answers 400 with the sentence; a refused
   * intent is never stored or queued.
   */
  ipv6: wanIpv6Schema.nullable().optional(),
});
export type CreateOnuVlanRequest = z.infer<typeof createOnuVlanSchema>;

export const portKindSchema = z.enum(['ethernet', 'wifi']);
export type PortKind = z.infer<typeof portKindSchema>;

/**
 * How a port treats VLAN tags.
 *
 * access  — one untagged VLAN, the normal subscriber port
 * trunk   — passes tags through untouched, for a downstream switch or router
 * hybrid  — an untagged default VLAN plus additional tagged ones
 */
/**
 * How a port treats VLAN tags — and, in `lan`'s case, whether it carries a
 * service VLAN at all.
 *
 * `lan` is the ONU's own LAN: the port sits behind the ONU's routing stack and
 * is served by it, rather than carrying a subscriber VLAN out to whatever is
 * plugged in. It is the right default on a routed VLAN, where the ONU
 * terminates the connection and the sockets behind it are just LAN.
 *
 * The other three are the tag question: `access` is one untagged VLAN, `trunk`
 * passes tags through, `hybrid` is an untagged default plus tagged extras.
 */
export const portModeSchema = z.enum(['lan', 'access', 'trunk', 'hybrid']);
export type PortMode = z.infer<typeof portModeSchema>;

export const PORT_MODE_LABELS: Record<PortMode, string> = {
  lan: 'LAN',
  access: 'Access',
  trunk: 'Trunk',
  hybrid: 'Hybrid',
};

/**
 * Who answers DHCP on an Ethernet socket — SmartOLT's four choices, which are
 * ZTE's `dhcp-ip ethuni eth_0/n …` line (2026-10-16, operator: "add the DHCP
 * selector on ports like smartolt"): `isp` = `from-internet` (the address
 * comes from upstream, the ONU is a bridge for it), `onu` = `from-onu` (the
 * ONU's own server), `forbidden` = `forbidden` (no DHCP crosses the socket),
 * `none` = no line at all (the chassis's "No control"). Null on a record is
 * AUTOMATIC: the platform's own reading — a `lan` socket from the ONU, a
 * socket on a bridged VLAN from the ISP, a socket on a routed VLAN from the
 * ONU. A Huawei chassis has no such line, so it takes only automatic.
 */
export const portDhcpSchema = z.enum(['isp', 'onu', 'forbidden', 'none']);
export type PortDhcp = z.infer<typeof portDhcpSchema>;

export const PORT_DHCP_LABELS: Record<PortDhcp, string> = {
  isp: 'From ISP',
  onu: 'From ONU',
  forbidden: 'Forbidden',
  none: 'No control',
};

/**
 * What a port should be before anybody has said otherwise.
 *
 * A routed VLAN means the ONU terminates the connection, so its sockets are the
 * ONU's own LAN. A bridged one is handed straight out, so the port carries it
 * untagged — which is `access`, and is what the overwhelming majority of
 * subscriber ports are.
 */
export function defaultPortMode(
  vlans: Array<{ vlanId: number; mode: VlanMode; purpose: VlanPurpose }>,
): { mode: PortMode; vlan: number | null } {
  // A management VLAN is the platform's path to the ONU, never a socket's.
  const subscriber = vlans.filter((v) => v.purpose !== 'management');
  const routed = subscriber.find((v) => v.mode === 'route');
  if (routed) return { mode: 'lan', vlan: null };
  const bridged = subscriber[0];
  // Nothing attached is also an answer: `access` with no VLAN is a mode the
  // chassis cannot be given and the API refuses, so an ONU with no subscriber
  // VLAN yet starts as LAN — which is valid on its own and is what a routed
  // ONU ends up as anyway.
  if (!bridged) return { mode: 'lan', vlan: null };
  return { mode: 'access', vlan: bridged.vlanId };
}

export const onuPortSchema = z.object({
  id: z.string(),
  /** TR-069 owner. Null when the intent belongs to an ONU with no ACS record. */
  deviceId: z.string().nullable(),
  /** OLT-side owner. Exactly one of the two is set. */
  onuId: z.string().nullable().optional(),
  /** Vendor port name, e.g. `eth_0/1` or `wifi_0/1`. */
  port: z.string(),
  kind: portKindSchema,
  label: z.string().nullable(),
  mode: portModeSchema,
  /** Untagged VLAN. Required for access and hybrid, ignored for trunk. */
  pvid: z.number().int().min(1).max(4094).nullable(),
  /** Additional tagged VLANs, hybrid only. */
  taggedVlans: z.array(z.number().int().min(1).max(4094)),
  enabled: z.boolean(),
  /** Who answers DHCP on the socket; null = automatic (`portDhcpSchema`). Ethernet only. */
  dhcp: portDhcpSchema.nullable().optional(),
  updatedAt: z.number().int(),
  /**
   * False for a socket the ONU has that nobody has set up yet.
   *
   * Every LAN port an ONU has is listed, whether or not this platform holds
   * intent for it — an engineer looking at a four-port ONU expects to see four
   * ports and choose one, not an empty list and a row of "add" buttons for
   * hardware that is already there. An unconfigured row carries the default
   * treatment and no id; saving it is what creates it.
   */
  configured: z.boolean(),
  /**
   * A WiFi port's network as the router reports it (2026-10-14: "add wifi
   * ports to the ports page"): its name, band, whether it is on, and the
   * VLAN of the bridge the router binds it to (null when it rides no bridge —
   * the router's own LAN). Absent for an Ethernet port, and for a WiFi port
   * whose router did not answer.
   */
  network: z
    .object({
      ssid: z.string().nullable(),
      band: z.string().nullable(),
      on: z.boolean().nullable(),
      bridgeVlan: z.number().int().nullable(),
    })
    .nullable()
    .optional(),
  /**
   * An Ethernet socket's link as the router reports it (2026-10-14: "add
   * status to the lan ports … connected/plugged in or not … show negotiation
   * like 10M/100M/1G"): whether something is plugged in (`Status` Up /
   * NoLink), the negotiated rate in Mb/s and duplex where the make reports
   * them (Huawei `X_HW_Speed` "Auto_100", `X_HW_DuplexMode` "Auto_Full"; a
   * ZTE F670L reports only `MaxBitRate` "Auto", so null), and the byte
   * counters. Absent for WiFi, and for a socket whose router did not answer.
   * As of the ACS's last read of the LAN tree — the Ports tab's refresh
   * re-reads it.
   */
  link: z
    .object({
      up: z.boolean().nullable(),
      speedMbps: z.number().int().nullable(),
      duplex: z.enum(['full', 'half']).nullable(),
      bytesIn: z.number().nullable(),
      bytesOut: z.number().nullable(),
    })
    .nullable()
    .optional(),
});
export type OnuPort = z.infer<typeof onuPortSchema>;

/** Changing an attached VLAN, without detaching and re-attaching it. */
export const updateOnuVlanSchema = z.object({
  mode: vlanModeSchema.optional(),
  addressing: vlanAddressingSchema.optional(),
  name: z.string().max(64).nullable().optional(),
  purpose: vlanPurposeSchema.optional(),
  /** See createOnuVlanSchema — the addressing's particulars, recorded on the row. */
  staticIp: z.string().max(64).nullable().optional(),
  staticMask: z.string().max(64).nullable().optional(),
  staticGateway: z.string().max(64).nullable().optional(),
  pppoeUsername: z.string().max(128).nullable().optional(),
  pppoePassword: z.string().max(128).nullable().optional(),
  serviceVlanId: z.number().int().min(1).max(4094).nullable().optional(),
  /**
   * Absent = the IPv6 record is untouched; null = back to not managed; an
   * intent = gated by `ipv6Refusals` before it is stored. A PATCH carrying
   * ONLY this field does not push the ONU's service config to the chassis —
   * the IPv6 record has no chassis half.
   */
  ipv6: wanIpv6Schema.nullable().optional(),
  /**
   * Who manages the WAN on this VLAN. Recorded only — like `ipv6`, a PATCH
   * carrying nothing else pushes nothing to the chassis; the WAN apply
   * (`omci-wan` with `owner`) is what moves the WAN.
   */
  wanOwner: wanOwnerSchema.nullable().optional(),
  /**
   * Correct the RECORD without touching the chassis (2026-10-21). A record
   * imported from another system, or made before the platform could see the
   * router, says DHCP where the router runs PPPoE; the operator makes the
   * record match what works, and nothing is pushed — the WAN is the router's
   * and the chassis lines are not re-rendered for it. Audited with the flag.
   */
  recordOnly: z.boolean().optional(),
});
export type UpdateOnuVlanRequest = z.infer<typeof updateOnuVlanSchema>;

/**
 * Canonicalise an ONU port name to the stored form (`eth_0/1`, `wifi_0/1`).
 *
 * The chassis renderers accept only `^(eth|wifi)_\d+\/\d+$`, and a value that
 * does not match ABORTS the render of the whole ONU — so one malformed row (a
 * stray `eth1` seeded out-of-band during testing) wedges resync AND recreate
 * entirely, on both vendors. This is the single place that maps a recoverable
 * shorthand back to the canonical name, used on write (so a bad value can never
 * be stored) and on read (so a legacy row cannot wedge a render):
 *  - already canonical (`eth_0/1`, `wifi_2/3`) → unchanged
 *  - a kind hint plus a port number (`eth1`, `eth 1`, `eth-1`, `lan1`, `wifi1`,
 *    `wlan1`, `ssid1`) → `<eth|wifi>_0/<n>`
 *  - a slot/port pair (`eth_1/2`) → `<eth|wifi>_<slot>/<port>`
 * Returns null for anything with no recognizable port number — the caller
 * leaves that to the renderer's own refusal, so genuine garbage still fails
 * loudly rather than being coerced to the wrong socket.
 */
export function normalizeOnuPortName(raw: string): string | null {
  const s = raw.trim().toLowerCase();
  const canon = /^(eth|wifi)_(\d+)\/(\d+)$/.exec(s);
  if (canon) return `${canon[1]}_${canon[2]}/${canon[3]}`;
  const m = /^(eth(?:ernet)?|lan|wifi|wlan|wl|ssid)[\s_/-]*(\d+)(?:\/(\d+))?$/.exec(s);
  if (!m) return null;
  const kind = /^(wifi|wlan|wl|ssid)/.test(m[1]!) ? 'wifi' : 'eth';
  const slot = m[3] !== undefined ? m[2] : '0';
  const port = m[3] !== undefined ? m[3] : m[2];
  return `${kind}_${slot}/${port}`;
}

export const upsertOnuPortSchema = z.object({
  port: z
    .string()
    .min(1)
    .max(40)
    .transform((v, ctx) => {
      const name = normalizeOnuPortName(v);
      if (name === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Not a recognizable ONU port name — use eth_0/1 or wifi_0/1.',
        });
        return z.NEVER;
      }
      return name;
    }),
  kind: portKindSchema,
  label: z.string().max(60).nullable().optional(),
  mode: portModeSchema,
  pvid: z.coerce.number().int().min(1).max(4094).nullable().optional(),
  taggedVlans: z.array(z.coerce.number().int().min(1).max(4094)).max(32).default([]),
  enabled: z.boolean().default(true),
  /** Who answers DHCP on an Ethernet socket; absent or null keeps the automatic reading. */
  dhcp: portDhcpSchema.nullable().optional(),
});
export type UpsertOnuPortRequest = z.infer<typeof upsertOnuPortSchema>;

/** What will be sent to the OLT, shown before anything is applied. */
export const onuConfigPlanSchema = z.object({
  /** The owner the plan was built for — a device id or an ONU id. */
  deviceId: z.string(),
  /** The OLT this ONU is on, when SimpleACS knows it. */
  oltId: z.string().nullable(),
  oltName: z.string().nullable(),
  /** Vendor interface, e.g. `gpon-onu_1/3/15:13`. */
  interfaceName: z.string().nullable(),
  commands: z.array(z.string()),
  /** Anything that prevents this from being applied. */
  blockers: z.array(z.string()),
  warnings: z.array(z.string()),
});
export type OnuConfigPlan = z.infer<typeof onuConfigPlanSchema>;

export const onuConfigApplyResultSchema = z.object({
  applied: z.boolean(),
  commands: z.array(z.string()),
  /** Exactly what the OLT said, so a failure is diagnosable. */
  transcript: z.string(),
  errors: z.array(z.string()),
});
export type OnuConfigApplyResult = z.infer<typeof onuConfigApplyResultSchema>;

// --- Authorise presets ---------------------------------------------------

/**
 * The authorise wizard's answers under a name — the standard package, the
 * usual VLANs, route + DHCP — applied in one tap and editable afterwards.
 * VLANs are applied only where the chassis carries them.
 */
/**
 * The chassis family a preset was built against.
 *
 * Null is a real answer and the safest default: a preset with no vendor was
 * built with no chassis in mind and travels everywhere, carrying only what the
 * target chassis can be checked to hold.
 */
export const presetVendorSchema = z.enum(['zte', 'huawei']);
export type PresetVendor = z.infer<typeof presetVendorSchema>;

/** How the ONU is managed after it is authorised. */
export const presetManagementModeSchema = z.enum(['tr069', 'omci']);
export type PresetManagementMode = z.infer<typeof presetManagementModeSchema>;

export const authorizePresetSchema = z.object({
  id: z.string(),
  orgId: z.string().nullable(),
  name: z.string(),
  onuType: z.string().nullable(),
  upstreamProfileId: z.string().nullable(),
  downstreamProfileId: z.string().nullable(),
  vlanMode: vlanModeSchema,
  addressing: vlanAddressingSchema,
  dataVlan: z.number().int().nullable(),
  managementVlan: z.number().int().nullable(),
  /** zte | huawei | null. See `applyPreset` for what a mismatch costs. */
  vendor: presetVendorSchema.nullable().optional(),
  zoneId: z.string().nullable().optional(),
  /**
   * How the ONU is managed once it is on service — TR-069 or OMCI-only.
   *
   * Null means the preset does not decide, and authorising leaves management
   * exactly as the chassis's own defaults left it. When it IS set, authorising
   * performs the switch through the same per-ONU management write the ONU page
   * uses, and the mode is recorded only once that write comes back accepted.
   */
  managementMode: presetManagementModeSchema.nullable().optional(),
  /** ONUs actually authorised from it — not times the dropdown was touched. */
  useCount: z.number().int().optional(),
  lastUsedAt: z.number().int().nullable().optional(),
  createdAt: z.number().int(),
});
export type AuthorizePreset = z.infer<typeof authorizePresetSchema>;

export const createAuthorizePresetSchema = z.object({
  name: z.string().trim().min(1).max(60),
  onuType: z.string().max(40).nullable().optional(),
  upstreamProfileId: z.string().max(64).nullable().optional(),
  downstreamProfileId: z.string().max(64).nullable().optional(),
  vlanMode: vlanModeSchema.default('route'),
  addressing: vlanAddressingSchema.default('dhcp'),
  dataVlan: z.number().int().min(1).max(4094).nullable().optional(),
  managementVlan: z.number().int().min(1).max(4094).nullable().optional(),
  /**
   * Recorded from the OLT the operator was standing on when they saved it —
   * the vendor whose VLAN list and ONU types they were choosing from. Nothing
   * asks them for it; being asked would only invite the wrong answer.
   */
  vendor: presetVendorSchema.nullable().optional(),
  zoneId: z.string().max(64).nullable().optional(),
  managementMode: presetManagementModeSchema.nullable().optional(),
});
export type CreateAuthorizePresetInput = z.infer<typeof createAuthorizePresetSchema>;

/**
 * Editing a saved preset — `PATCH /api/onu-presets/:id`, under `olt.manage`.
 *
 * Every field optional and, unlike create, NONE carry a default: an absent field
 * is "leave it as it was", not "reset to route/dhcp". At least one field is
 * required. The TR-069-needs-a-management-VLAN rule is re-checked server-side
 * against the MERGED result, so it holds however few fields the patch carries.
 */
export const updateAuthorizePresetSchema = z
  .object({
    name: z.string().trim().min(1).max(60).optional(),
    onuType: z.string().max(40).nullable().optional(),
    upstreamProfileId: z.string().max(64).nullable().optional(),
    downstreamProfileId: z.string().max(64).nullable().optional(),
    vlanMode: vlanModeSchema.optional(),
    addressing: vlanAddressingSchema.optional(),
    dataVlan: z.number().int().min(1).max(4094).nullable().optional(),
    managementVlan: z.number().int().min(1).max(4094).nullable().optional(),
    vendor: presetVendorSchema.nullable().optional(),
    zoneId: z.string().max(64).nullable().optional(),
    managementMode: presetManagementModeSchema.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'Send at least one field to change — an empty edit is not a change.',
  });
export type UpdateAuthorizePresetInput = z.infer<typeof updateAuthorizePresetSchema>;

// --- Applying a preset ---------------------------------------------------

/**
 * What a preset is being applied to: one chassis, and the libraries the org
 * holds. Every list here is something a preset value is CHECKED against, never
 * something it overrides — a VLAN this chassis does not carry cannot reach a
 * subscriber however carefully it was typed into a preset months ago.
 */
export interface PresetTarget {
  /** The chassis family — zte | huawei — or null when it is not known. */
  vendor: string | null;
  /** VLAN ids this chassis carries that a subscriber may ride. */
  subscriberVlans: number[];
  /** VLAN ids this chassis carries for management. */
  managementVlans: number[];
  /** ONU type names the hardware library holds for this org. */
  onuTypes: string[];
  /** Speed profile ids this org holds, both directions. */
  speedProfileIds: string[];
  /** Zone ids this org holds. */
  zoneIds?: string[];
}

/** A field the preset named that this target could not honour, and why. */
export interface PresetFieldDrop {
  field:
    | 'onuType'
    | 'upstreamProfileId'
    | 'downstreamProfileId'
    | 'dataVlan'
    | 'managementVlan'
    | 'managementMode'
    | 'zoneId';
  wanted: string | number;
  reason: string;
}

/**
 * The authorise input a preset resolves to.
 *
 * A key that is absent means "leave whatever is there" — a preset that says
 * nothing about the ONU type must not wipe the type the wizard worked out from
 * the serial, and one whose VLAN this chassis lacks must not blank the VLAN the
 * operator already chose. Only `vlanMode` and `addressing` are always present,
 * because a preset always holds both.
 */
export interface ResolvedAuthorizeInput {
  onuType?: string;
  upstreamProfileId?: string;
  downstreamProfileId?: string;
  vlanMode: VlanMode;
  addressing: VlanAddressing;
  dataVlan?: number;
  managementVlan?: number;
  /**
   * TR-069 or OMCI. Present only when the preset decides it AND the switch can
   * actually be performed on this target — see `applyPreset`.
   */
  managementMode?: PresetManagementMode;
  zoneId?: string;
}

export interface AppliedPreset {
  /** Merge over the form. Never a lock — a preset is a habit, not a rule. */
  values: ResolvedAuthorizeInput;
  dropped: PresetFieldDrop[];
  /** Plain sentences for the operator, worst first. */
  warnings: string[];
  /** The preset names a vendor and the target is a different one. */
  vendorMismatch: boolean;
}

export const PRESET_FIELD_LABELS: Record<PresetFieldDrop['field'], string> = {
  onuType: 'ONU type',
  upstreamProfileId: 'Upload speed',
  downstreamProfileId: 'Download speed',
  dataVlan: 'Subscriber VLAN',
  managementVlan: 'Management VLAN',
  managementMode: 'Management mode',
  zoneId: 'Zone',
};

/**
 * Resolve a preset against one chassis — the whole application path, in one
 * pure function so the wizard, the bulk authoriser and an ONU moved to another
 * OLT can never disagree about what a preset means.
 *
 * Two rules, both there to stop a preset producing a config that is wrong
 * rather than absent:
 *
 * 1. **Nothing is taken on trust.** Every value that names something outside
 *    the preset — a VLAN, a speed profile, an ONU type, a zone — is checked
 *    against what this target actually holds, and dropped with a reason if it
 *    is not there. The alternative is a subscriber authorised onto a VLAN the
 *    chassis does not carry, which looks like a successful authorisation and
 *    is a dead line.
 *
 * 2. **A vendor mismatch costs the ONU type and nothing else.** The ONU type
 *    is the one field whose MEANING changes with the chassis family: ZTE
 *    declares it inline on the PON port, while Huawei resolves the model to
 *    line and service profile ids that are per-chassis and are created on
 *    demand. Carrying a type across families would either be rejected late, at
 *    the chassis, or bind an ONT to the wrong profile — so it is dropped here,
 *    early, where the operator can see it and pick again (the wizard's own
 *    guess from the serial is usually right anyway). Everything else — VLANs,
 *    speed profiles, routed-vs-bridged, addressing, zone — is vendor-neutral
 *    or already checked against this chassis, so it survives.
 *
 * The management VLAN needs no vendor rule of its own: it is per CHASSIS, not
 * per vendor (this fleet runs 10, 2002 and 90 on different boxes), and rule 1
 * already refuses one the target does not carry.
 *
 * TR-069 vs OMCI IS part of a preset, and it is the one field that makes the
 * authorise path do extra work rather than fill a box: the mode is performed by
 * the same per-ONU management write the ONU page uses, straight after the
 * authorisation lands, and the ONU's recorded mode is written only once the
 * chassis has accepted it. That is why it can be dropped here for a reason
 * nothing else is dropped for — an unreachable ACS is not a wrong value, it is
 * a switch that would be accepted and do nothing:
 *
 *   TR-069 without a management VLAN this chassis carries is exactly that trap.
 *   ZTE takes the `tr069-mgmt` lines and the ONU, having no address, never
 *   informs; Huawei binds the server profile and, with no `ont ipconfig`, the
 *   ONT never reaches the ACS either. Both look like success. So if the
 *   management VLAN did not survive the checks above, TR-069 goes with it and
 *   says why — leaving the ONU OMCI-managed and honest instead of "waiting"
 *   for an inform that can never arrive.
 *
 * OMCI needs nothing, and both vendors implement it (`tr069-mgmt 1 state lock`
 * on ZTE, `undo ont tr069-server-config` on Huawei), so it always survives.
 */
export function applyPreset(preset: AuthorizePreset, target: PresetTarget): AppliedPreset {
  const dropped: PresetFieldDrop[] = [];
  const warnings: string[] = [];
  const presetVendor = preset.vendor ?? null;
  const vendorMismatch =
    presetVendor !== null && target.vendor !== null && presetVendor !== target.vendor;

  if (vendorMismatch) {
    warnings.push(
      `“${preset.name}” was built on a ${presetVendor!.toUpperCase()} OLT and this one is ${String(target.vendor).toUpperCase()}. Its VLANs and packages still apply where this chassis has them; the ONU type does not travel between vendors and is left for you to choose.`,
    );
  }

  const values: ResolvedAuthorizeInput = {
    vlanMode: preset.vlanMode,
    addressing: preset.addressing,
  };

  if (preset.onuType) {
    if (vendorMismatch) {
      dropped.push({
        field: 'onuType',
        wanted: preset.onuType,
        reason: `an ONU type means a different thing on ${String(target.vendor)} than on ${presetVendor}`,
      });
    } else if (target.onuTypes.includes(preset.onuType)) {
      values.onuType = preset.onuType;
    } else {
      // Kept, not dropped: the wizard accepts a hand-typed model for hardware
      // the library has not met, and refusing it here would make a preset less
      // capable than typing the same thing by hand.
      values.onuType = preset.onuType;
      warnings.push(
        `The ONU type library has no entry for ${preset.onuType}. Authorising will refuse it on a Huawei OLT, which declares an ONT's ports from the library.`,
      );
    }
  }

  for (const field of ['upstreamProfileId', 'downstreamProfileId'] as const) {
    const wanted = preset[field];
    if (!wanted) continue;
    if (target.speedProfileIds.includes(wanted)) values[field] = wanted;
    else
      dropped.push({
        field,
        wanted,
        reason: 'that speed profile no longer exists',
      });
  }

  if (preset.dataVlan != null) {
    if (target.subscriberVlans.includes(preset.dataVlan)) values.dataVlan = preset.dataVlan;
    else
      dropped.push({
        field: 'dataVlan',
        wanted: preset.dataVlan,
        reason: 'this OLT does not carry it as a subscriber VLAN',
      });
  }

  if (preset.managementVlan != null) {
    if (target.managementVlans.includes(preset.managementVlan))
      values.managementVlan = preset.managementVlan;
    else
      dropped.push({
        field: 'managementVlan',
        wanted: preset.managementVlan,
        reason: 'this OLT manages ONUs on a different VLAN',
      });
  }

  if (preset.managementMode === 'omci') {
    values.managementMode = 'omci';
  } else if (preset.managementMode === 'tr069') {
    // Deliberately checked against the RESOLVED VLAN, not the preset's: a
    // management VLAN dropped two branches up is the very case that turns
    // "put it on TR-069" into an ONU that informs to nobody.
    if (values.managementVlan !== undefined) values.managementMode = 'tr069';
    else
      dropped.push({
        field: 'managementMode',
        wanted: 'tr069',
        reason:
          'TR-069 needs a management VLAN this OLT carries, and none survived — the chassis would accept the change and the ONU would never inform',
      });
  }

  if (preset.zoneId) {
    // No zone list given means the caller is not offering placement at all,
    // which is not the same as the zone being gone.
    if (!target.zoneIds || target.zoneIds.includes(preset.zoneId)) values.zoneId = preset.zoneId;
    else
      dropped.push({ field: 'zoneId', wanted: preset.zoneId, reason: 'that zone no longer exists' });
  }

  for (const drop of dropped) {
    warnings.push(`${PRESET_FIELD_LABELS[drop.field]} — ${drop.reason}, so it was left as it is.`);
  }

  return { values, dropped, warnings, vendorMismatch };
}
